import type { PropsWithChildren } from 'react';
import React, { Children } from 'react';
import type { SwiperOptions } from 'swiper';
import { FreeMode, Navigation } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import type { Maybe } from '@/lib/types';
import { getBreakpointSize } from '@/theme';

import * as S from './NewSlider.styles';

export interface NewSliderProps {
  children: React.ReactNode;
  disableGutters?: boolean;
  variantColor?: 'light' | 'dark';
}

export const NewSlider = ({
  children,
  disableGutters,
  variantColor = 'light',
}: NewSliderProps) => {
  return (
    <S.Wrapper
      data-test-id='NewSliderWrapper'
      disableGutters={disableGutters}
      variantColor={variantColor}
    >
      {children}
    </S.Wrapper>
  );
};

const Header = ({ children }: PropsWithChildren) => {
  return <S.Title data-test-id='NewSliderHeader'>{children}</S.Title>;
};

export interface SlideProps {
  breakpoints?: Maybe<SwiperOptions['breakpoints']>;
  children: React.ReactNode;
  separator?: boolean;
  slidesPerView?: Maybe<number>;
  spaceBetween?: number;
}

const Slider = ({
  breakpoints,
  children,
  separator,
  slidesPerView: initialSlidesPerView,
  spaceBetween = 20,
}: SlideProps) => {
  const slidesPerView = initialSlidesPerView || 3;
  const itemsCount = Children.count(children);
  const defaultBreakpoints = {
    [getBreakpointSize('xs')]: {
      slidesPerView: 1.2,
    },
    [getBreakpointSize('sm')]: {
      slidesPerView: 2.2,
    },
    [getBreakpointSize('md')]: {
      slidesPerView: 2.2,
    },
    [getBreakpointSize('lg')]: {
      slidesPerView: slidesPerView,
    },
  };

  return (
    <S.StyledSwiper
      $hideNavigation={slidesPerView >= itemsCount}
      breakpoints={breakpoints || defaultBreakpoints}
      data-test-id='NewSliderStyledSwiper'
      freeMode={true}
      modules={[Navigation, FreeMode]}
      navigation
      separator={separator ? 'true' : 'false'}
      spaceBetween={spaceBetween}
      watchSlidesProgress
    >
      {Children.map(
        children,
        (child, index) => (
          // prettier-ignore
          <SwiperSlide key={index} data-test-id={`NewSliderSwiperSlide-${index}`}>{child}</SwiperSlide>
        ), //NOSONAR
      )}
    </S.StyledSwiper>
  );
};

NewSlider.Header = Header;
NewSlider.Slider = Slider;

NewSlider.displayName = 'NewSlider';
